import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { KPIData } from './types';
import { formatCurrency, formatPercentage } from './FinancialDashboardController';

const useStyles = makeStyles(() => ({
  kpiContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    margin: '16px 0',
  },
  kpiCard: {
    flex: '1 1 200px',
    minWidth: '200px',
    maxWidth: '280px',
    border: '2px solid #ccc',
    borderRadius: '8px',
    padding: '20px',
    backgroundColor: '#fff',

    '& .kpi-label': {
      fontSize: '13px',
      fontWeight: 600,
      color: '#666',
      textTransform: 'uppercase',
      marginBottom: '8px',
    },
    '& .kpi-value': {
      fontSize: '28px',
      fontWeight: 700,
      color: '#333',
      marginBottom: '8px',
    },
    '& .kpi-current': {
      fontSize: '13px',
      fontWeight: 600,
      color: '#555',
      marginBottom: '6px',
    },
    '& .kpi-change': {
      fontSize: '13px',
      fontWeight: 600,
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: '4px',
    },
    '& .kpi-change.positive': {
      color: '#2e7d32',
      backgroundColor: 'rgba(46, 125, 50, 0.1)',
    },
    '& .kpi-change.negative': {
      color: '#c62828',
      backgroundColor: 'rgba(198, 40, 40, 0.1)',
    },
    '& .kpi-change.neutral': {
      color: '#666',
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
    },
  },
}));

function getChangeClass(value: number): string {
  if (value > 0) return 'positive';
  if (value < 0) return 'negative';
  return 'neutral';
}

interface KPICardsProps {
  kpiData: KPIData;
  showCash: boolean;
}

function KPICards({ kpiData, showCash }: KPICardsProps) {
  const classes = useStyles();
  const { t } = useTranslation();

  const cards = [
    {
      label: t('financialdashboard.total-income'),
      value: formatCurrency(kpiData.totalIncome),
      changePercent: kpiData.thisMonthIncomeGrowth,
      changeLabel: t('financialdashboard.vs-previous-month'),
    },
    {
      label: t('financialdashboard.total-sales'),
      value: kpiData.totalSales.toLocaleString('nl-NL'),
      changePercent: kpiData.thisMonthSalesGrowth,
      changeLabel: t('financialdashboard.vs-previous-month'),
    },
    {
      label: t('financialdashboard.avg-per-month'),
      value: formatCurrency(kpiData.avgPerMonth),
      currentAmount: kpiData.currentMonthIncome > 0 ? formatCurrency(kpiData.currentMonthIncome) : null,
      currentLabel: t('financialdashboard.last-month'),
      changePercent: kpiData.currentMonthVsAvg,
      changeLabel: t('financialdashboard.vs-average'),
      hideChange: kpiData.currentMonthIncome === 0,
    },
    {
      label: t('financialdashboard.avg-per-week'),
      value: formatCurrency(kpiData.avgPerWeek),
      currentAmount: kpiData.currentWeekIncome > 0 ? formatCurrency(kpiData.currentWeekIncome) : null,
      currentLabel: t('financialdashboard.last-week'),
      changePercent: kpiData.currentWeekVsAvg,
      changeLabel: t('financialdashboard.vs-average'),
      hideChange: kpiData.currentWeekIncome === 0,
    },
    {
      label: t('financialdashboard.todays-sales'),
      value: kpiData.todaySales.toLocaleString('nl-NL'),
      extraLines: [
        `${formatCurrency(kpiData.todayAmountPaid)} ${t('financialdashboard.todays-sales-paid')}`,
        ...(showCash && kpiData.todayAmountCash > 0
          ? [`${formatCurrency(kpiData.todayAmountCash)} ${t('financialdashboard.todays-sales-cash')}`]
          : []),
      ],
      hideChange: true,
    },
    {
      label: t('financialdashboard.avg-items-per-transaction'),
      value: kpiData.avgItemsPerTransaction.toLocaleString('nl-NL', { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
      currentAmount: kpiData.lastMonthAvgItems > 0
        ? kpiData.lastMonthAvgItems.toLocaleString('nl-NL', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
        : null,
      currentLabel: t('financialdashboard.last-month'),
      changePercent: kpiData.lastMonthAvgItemsVsAvg,
      changeLabel: t('financialdashboard.vs-average'),
      hideChange: kpiData.lastMonthAvgItems === 0,
    },
  ];

  return (
    <div className={classes.kpiContainer}>
      {cards.map((card, index) => {
        const cls = getChangeClass(card.changePercent ?? 0);
        return (
          <div key={index} className={classes.kpiCard}>
            <div className="kpi-label">{card.label}</div>
            <div className="kpi-value">{card.value}</div>
            {card.currentAmount && (
              <div className="kpi-current">
                {card.currentAmount} {card.currentLabel}
              </div>
            )}
            {card.extraLines?.map((line, i) => (
              <div key={i} className="kpi-current">
                {line}
              </div>
            ))}
            {!card.hideChange && (
              <span className={`kpi-change ${cls}`}>
                {formatPercentage(card.changePercent ?? 0)} {card.changeLabel}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default KPICards;
